<script>
  import { onMount, tick, createEventDispatcher } from "svelte";
  import CloseButton from "./CloseButton.svelte";
  import VideoPlayIcon from "./icons/VideoPlayIcon.svelte";
  import VideoPauseIcon from "./icons/VideoPauseIcon.svelte";
  import VideoReplayIcon from "./icons/VideoReplayIcon.svelte";
  import VideoMuteIcon from "./icons/VideoMuteIcon.svelte";
  import VideoUnmuteIcon from "./icons/VideoUnmuteIcon.svelte";
  import { makeEmbedUrl } from "./utils.js";
  import UA from "ua-parser-js";

  export let src = false;
  export let content = null;
  export let onClose;
  export let hasDescription = false;
  export let textColor = "#ffffff";
  export let altText = "";

  const dispatch = createEventDispatcher();
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  $: effectiveSrc = content?.videoAssets?.[0]?.url || src;
  $: isVisible = !!effectiveSrc;
  $: dialogLabel =
    altText && altText.trim()
      ? altText
      : content?.platform
        ? `${content.platform} content: ${content.authorName || "Social Media Post"}`
        : "Video player";

  let playerRef;
  let videoContainer;
  let isIframe = false;
  let isYoutube = false;
  let isVimeo = false;
  let isWistia = false;
  let youtubePlayer;
  let vimeoPlayer;
  let videoId = "";
  let uniqueId = `video-${Math.random().toString(36).substr(2, 9)}`;
  let originalSrc = effectiveSrc;
  let processedSrc = makeEmbedUrl(effectiveSrc, true) || effectiveSrc;

  let isPlaying = true;
  let isMuted = false;
  let progress = 0;
  let hasEnded = false;

  let sourceWidth = 1280;
  let sourceHeight = 720;
  let videoDimensions = { width: 720, height: 405, aspectRatio: 16 / 9 };
  let dimensionsReady = false;
  let initToken = 0;

  const OEMBED_FETCH_TIMEOUT_MS = 5000;

  function needsOEmbed(url) {
    return /youtube|youtu\.be|vimeo/i.test(url);
  }

  function calculateVideoDimensions() {
    const maxScale = isMobile ? 0.85 : 0.7;
    const windowWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
    const windowHeight = typeof window !== "undefined" ? window.innerHeight : 720;

    let aspectWidth = sourceWidth;
    let aspectHeight = sourceHeight;

    if (content?.videoAssets?.[0]?.width) {
      aspectWidth = content.videoAssets[0].width;
      aspectHeight = content.videoAssets[0].height || aspectHeight;
    }

    const aspectRatio = aspectWidth / aspectHeight;
    const maxWidth = windowWidth * maxScale;
    const maxHeight = windowHeight * maxScale;

    // Scale to the largest size that fits the viewport. oEmbed APIs return
    // thumbnail dimensions (e.g. 240px wide), not display size — use only aspect ratio.
    let finalWidth = maxWidth;
    let finalHeight = finalWidth / aspectRatio;

    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * aspectRatio;
    }

    videoDimensions = { width: finalWidth, height: finalHeight, aspectRatio };
  }

  /** Canonical Vimeo page URL for oEmbed (works for watch links and player.vimeo.com embeds). */
  function vimeoUrlForOEmbed(urlString) {
    try {
      const u = new URL(
        urlString.startsWith("//") ? `https:${urlString}` : urlString
      );
      if (u.hostname.includes("player.vimeo.com")) {
        const m = u.pathname.match(/\/video\/(\d+)/);
        if (m) return `https://vimeo.com/${m[1]}`;
      }
      return urlString;
    } catch {
      return urlString;
    }
  }

  async function fetchOEmbedAspectRatio() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OEMBED_FETCH_TIMEOUT_MS);

    try {
      let oEmbedUrl;
      if (/youtube|youtu\.be/i.test(originalSrc)) {
        oEmbedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(originalSrc)}&format=json`;
      } else if (/vimeo/i.test(originalSrc)) {
        const vimeoPage = vimeoUrlForOEmbed(originalSrc);
        oEmbedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoPage)}`;
      } else {
        return null;
      }

      const response = await fetch(oEmbedUrl, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`oEmbed request failed: ${response.status}`);
      }

      const data = await response.json();

      const width = data.width;
      const height = data.height;
      if (width && height) {
        sourceWidth = width;
        sourceHeight = height;
      }
      calculateVideoDimensions();
      return width && height
        ? {
            width,
            height,
            aspectRatio: width / height,
            title: data.title,
            author: data.author_name,
          }
        : null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.warn(
          `oEmbed request timed out after ${OEMBED_FETCH_TIMEOUT_MS}ms; using 16:9 fallback`
        );
      } else {
        console.error("Error fetching video aspect ratio:", error);
      }
      calculateVideoDimensions();
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  function handleEmbedMessage(event) {
    if (!isVisible || !isIframe || isYoutube || (!isVimeo && !isWistia)) return;

    let data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }

    if (!data || typeof data !== "object") return;

    if (data.event === "finish" || data.event === "ended") {
      close();
      return;
    }

    if (data.method === "end" || data.event === "end") {
      close();
    }
  }

  function loadVimeoAPI() {
    return new Promise((resolve) => {
      if (window.Vimeo?.Player) {
        resolve();
        return;
      }

      const existing = document.querySelector(
        'script[src*="player.vimeo.com/api/player.js"]'
      );
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.onload = () => resolve();
      document.body.appendChild(script);
    });
  }

  function teardownVimeoPlayer() {
    if (!vimeoPlayer) return;

    vimeoPlayer.off("ended");
    vimeoPlayer = null;
  }

  async function setupVimeoPlayer() {
    if (!isVisible || !isVimeo) return;

    await loadVimeoAPI();
    await tick();

    if (!isVisible || !isVimeo || !videoContainer || !window.Vimeo?.Player) {
      setTimeout(setupVimeoPlayer, 100);
      return;
    }

    teardownVimeoPlayer();

    try {
      vimeoPlayer = new window.Vimeo.Player(videoContainer);
      vimeoPlayer.on("ended", close);
    } catch (error) {
      console.error("Vimeo player setup failed:", error);
    }
  }

  function bindWistiaEndHandler() {
    const match = processedSrc.match(/\/embed\/iframe\/([^/?]+)/i);
    const mediaId = match?.[1];
    if (!mediaId) return;

    window._wq = window._wq || [];
    window._wq.push({
      id: mediaId,
      onReady: (player) => {
        player.bind("end", () => {
          close();
        });
      },
    });

    if (!document.querySelector('script[src*="wistia.com/assets/external/E-v1.js"]')) {
      const script = document.createElement("script");
      script.src = "https://fast.wistia.com/assets/external/E-v1.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }

  function loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          resolve();
        };
      } else {
        resolve();
      }
    });
  }

  function getPlayerVars(url) {
    const playerVars = {
      enablejsapi: 1,
      origin: window.location.origin,
      widget_referrer: window.location.origin,
      autoplay: 1,
      rel: 0,
      modestbranding: 1,
    };

    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;

      const start = params.get("start");
      if (start) {
        const startSeconds = parseInt(start, 10);
        if (!isNaN(startSeconds) && startSeconds > 0) {
          playerVars.start = startSeconds;
        }
      }

      const end = params.get("end");
      if (end) {
        const endSeconds = parseInt(end, 10);
        if (!isNaN(endSeconds) && endSeconds > 0) {
          playerVars.end = endSeconds;
        }
      }

      const list = params.get("list");
      if (list) {
        playerVars.list = list;
      }

      const loop = params.get("loop");
      if (loop !== null) {
        playerVars.loop = loop === "1" ? 1 : 0;
      }

      const controls = params.get("controls");
      if (controls !== null) {
        const controlsValue = parseInt(controls, 10);
        if (!isNaN(controlsValue) && controlsValue >= 0 && controlsValue <= 2) {
          playerVars.controls = controlsValue;
        }
      }

      const mute = params.get("mute");
      if (mute !== null) {
        playerVars.mute = mute === "1" ? 1 : 0;
      }

      const ccLoadPolicy = params.get("cc_load_policy");
      if (ccLoadPolicy !== null) {
        playerVars.cc_load_policy = ccLoadPolicy === "1" ? 1 : 0;
      }

      const hl = params.get("hl");
      if (hl) {
        playerVars.hl = hl;
      }

      const autoplay = params.get("autoplay");
      if (autoplay !== null) {
        playerVars.autoplay = autoplay === "1" ? 1 : 0;
      }

      const rel = params.get("rel");
      if (rel !== null) {
        playerVars.rel = rel === "1" ? 1 : 0;
      }

      const modestbranding = params.get("modestbranding");
      if (modestbranding !== null) {
        playerVars.modestbranding = modestbranding === "1" ? 1 : 0;
      }
    } catch (e) {
      console.warn("Failed to parse YouTube URL for playerVars:", e);
    }

    return playerVars;
  }

  async function setupYouTubePlayer() {
    await loadYouTubeAPI();

    if (!window.YT || !window.YT.Player) {
      setTimeout(setupYouTubePlayer, 100);
      return;
    }

    if (youtubePlayer) {
      youtubePlayer.destroy();
      youtubePlayer = null;
    }

    if (!videoId) {
      console.warn("No video ID available");
      return;
    }

    const element = document.getElementById(uniqueId);
    if (!element) {
      setTimeout(setupYouTubePlayer, 100);
      return;
    }

    const playerVars = getPlayerVars(processedSrc);

    youtubePlayer = new YT.Player(uniqueId, {
      videoId,
      playerVars,
      events: {
        onReady: () => {},
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED) {
            close();
          }
        },
        onError: (event) => {
          console.error("YouTube player error:", event.data);
        },
      },
    });
  }

  async function initializeForSrc(srcUrl) {
    const token = ++initToken;
    dimensionsReady = false;
    sourceWidth = 1280;
    sourceHeight = 720;
    isPlaying = true;
    isMuted = false;
    progress = 0;
    hasEnded = false;

    originalSrc = srcUrl;
    processedSrc = makeEmbedUrl(srcUrl, true) || srcUrl;

    isYoutube = false;
    isVimeo = false;
    isWistia = false;
    isIframe = false;
    videoId = "";

    if (/youtube/i.test(processedSrc)) {
      isYoutube = true;
      isIframe = true;
      const match = processedSrc.match(
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i
      );
      videoId = match ? match[1] : "";
    } else if (/vimeo/i.test(processedSrc)) {
      isVimeo = true;
      isIframe = true;
    } else if (/wistia/i.test(processedSrc)) {
      isWistia = true;
      isIframe = true;
    }

    if (content?.videoAssets?.[0]?.width) {
      calculateVideoDimensions();
      dimensionsReady = true;
    } else if (needsOEmbed(originalSrc)) {
      await fetchOEmbedAspectRatio();
      if (token !== initToken) return;
      dimensionsReady = true;
    } else if (isIframe) {
      calculateVideoDimensions();
      dimensionsReady = true;
    }

    if (token !== initToken || !dimensionsReady) return;

    await tick();
    if (token !== initToken) return;

    if (isYoutube && videoId) {
      setupYouTubePlayer();
    } else if (isWistia) {
      bindWistiaEndHandler();
    }
  }

  $: if (effectiveSrc) {
    initializeForSrc(effectiveSrc);
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") close();
  }

  function handlePlayPause() {
    if (hasEnded) {
      hasEnded = false;
      progress = 0;
      if (playerRef) {
        playerRef.currentTime = 0;
      }
    }
    isPlaying = !isPlaying;

    if (playerRef) {
      if (isPlaying) {
        playerRef.play().catch((error) => {
          console.error("[Video Play Error]", error);
          isPlaying = false;
        });
      } else {
        playerRef.pause();
      }
    }
  }

  function handleMuteUnmute() {
    isMuted = !isMuted;
    if (playerRef) {
      playerRef.muted = isMuted;
    }
  }

  function handleTimeUpdate() {
    if (playerRef) {
      const currentTime = playerRef.currentTime;
      const duration = playerRef.duration;
      progress = (currentTime / duration) * 100;
    }
  }

  function handleVideoClick(e) {
    e.stopPropagation();

    if (e.target.closest(".control-button")) {
      return;
    }

    handlePlayPause();
  }

  function handleNativeEnded() {
    hasEnded = true;
    isPlaying = false;
    close();
  }

  function handleProgressBarClick(e) {
    e.stopPropagation();

    if (!playerRef) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;

    playerRef.currentTime = clickPosition * playerRef.duration;
    progress = clickPosition * 100;

    if (hasEnded) {
      hasEnded = false;
    }

    isPlaying = true;
    playerRef.play().catch((error) => {
      console.error("[Video Seek Error]", error);
      isPlaying = false;
    });
  }

  function handleLoadedMetadata() {
    if (playerRef && !isIframe) {
      sourceWidth = playerRef.videoWidth || 1280;
      sourceHeight = playerRef.videoHeight || 720;
      calculateVideoDimensions();
      dimensionsReady = true;
    }
  }

  function handleProbeLoadedMetadata(e) {
    if (!isVisible) return;
    const video = e.target;
    sourceWidth = video.videoWidth || 1280;
    sourceHeight = video.videoHeight || 720;
    calculateVideoDimensions();
    dimensionsReady = true;
  }

  function close() {
    if (youtubePlayer) {
      youtubePlayer.destroy();
      youtubePlayer = null;
    }
    teardownVimeoPlayer();

    isPlaying = true;
    isMuted = false;
    progress = 0;
    hasEnded = false;
    sourceWidth = 1280;
    sourceHeight = 720;
    dimensionsReady = false;

    src = false;
    onClose?.();
    dispatch("close");
  }

  onMount(() => {
    window.addEventListener("message", handleEmbedMessage);
    window.addEventListener("resize", calculateVideoDimensions);

    return () => {
      window.removeEventListener("message", handleEmbedMessage);
      window.removeEventListener("resize", calculateVideoDimensions);
      teardownVimeoPlayer();
      if (youtubePlayer) {
        youtubePlayer.destroy();
        youtubePlayer = null;
      }
    };
  });
</script>

{#if isVisible}
  <div
    class="popup-overlay"
    on:click={close}
    on:keydown={handleKeyDown}
    role="dialog"
    aria-modal="true"
    aria-label={dialogLabel}
  >
    {#if !dimensionsReady}
      <div class="loading-state" role="status" aria-label="Loading video">
        <div class="spinner" style="--spinner-color: {textColor}"></div>
      </div>
    {/if}

    {#if !dimensionsReady && !isIframe}
      <!-- svelte-ignore a11y-media-has-caption -->
      <video
        class="sizing-probe"
        src={processedSrc}
        preload="metadata"
        on:loadedmetadata={handleProbeLoadedMetadata}
        aria-hidden="true"
      />
    {/if}

    {#if dimensionsReady}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div class="social-popup" on:click|stopPropagation>
        <div class="social-content">
          <main class="social-embed-container">
            <div
              class="video-container"
              style="width: {videoDimensions.width}px; height: {videoDimensions.height}px"
              role="region"
              aria-label="Video player"
            >
            {#if isIframe}
              {#if isYoutube}
                <div
                  id={uniqueId}
                  class="embed-player"
                  bind:this={videoContainer}
                ></div>
              {:else}
                <iframe
                  id={uniqueId}
                  class="embed-player"
                  title="Video"
                  src={processedSrc}
                  allowfullscreen
                  allow="autoplay; fullscreen; picture-in-picture"
                  frameborder="0"
                  bind:this={videoContainer}
                  on:load={() => {
                    if (isVimeo) setupVimeoPlayer();
                  }}
                />
              {/if}
            {:else}
              <div
                class="video-wrapper"
                style="aspect-ratio: {videoDimensions.aspectRatio}"
                on:click={handleVideoClick}
                on:keydown={handleVideoClick}
                on:keyup={() => {}}
                role="application"
                aria-label="Video controls"
              >
                <!-- svelte-ignore a11y-media-has-caption -->
                <video
                  bind:this={playerRef}
                  src={processedSrc}
                  muted={isMuted}
                  autoplay={true}
                  on:timeupdate={handleTimeUpdate}
                  on:ended={handleNativeEnded}
                  on:loadedmetadata={handleLoadedMetadata}
                  on:play={() => {
                    isPlaying = true;
                  }}
                  on:pause={() => {
                    isPlaying = false;
                  }}
                  on:error={(e) => {
                    console.error("[Video Error Event]", e);
                  }}
                  playsinline
                  style="width: 100%; height: 100%;"
                  aria-label="Video content"
                />
                {#if hasEnded}
                  <div class="video-overlay" role="status" aria-label="Video ended">
                    <button
                      class="replay-button"
                      on:click|stopPropagation={handlePlayPause}
                      aria-label="Replay video"
                    >
                      <VideoReplayIcon size={48} color={textColor} />
                    </button>
                  </div>
                {/if}
                {#if !isPlaying && !hasEnded}
                  <div class="video-overlay" role="status" aria-label="Video paused">
                    <button
                      class="play-button"
                      on:click|stopPropagation={handlePlayPause}
                      aria-label="Play video"
                    >
                      <VideoPlayIcon size={48} color={textColor} />
                    </button>
                  </div>
                {/if}
                <button
                  class="control-button mute-unmute {isMuted ? 'always-visible' : ''}"
                  on:click|stopPropagation={handleMuteUnmute}
                  aria-label={isMuted ? "Unmute video" : "Mute video"}
                  aria-pressed={isMuted}
                >
                  {#if isMuted}
                    <VideoMuteIcon color={textColor} />
                  {:else}
                    <VideoUnmuteIcon color={textColor} />
                  {/if}
                </button>
                <div
                  class="video-controls"
                  role="toolbar"
                  aria-label="Video playback controls"
                >
                  <button
                    class="control-button play-pause"
                    on:click|stopPropagation={handlePlayPause}
                    aria-label={hasEnded
                      ? "Replay video"
                      : isPlaying
                        ? "Pause video"
                        : "Play video"}
                    aria-pressed={isPlaying}
                  >
                    {#if hasEnded}
                      <VideoReplayIcon size={24} color={textColor} />
                    {:else if isPlaying}
                      <VideoPauseIcon size={24} color={textColor} />
                    {:else}
                      <VideoPlayIcon size={24} color={textColor} />
                    {/if}
                  </button>
                  <!-- svelte-ignore a11y-interactive-supports-focus -->
                  <div
                    class="progress-bar"
                    on:click|stopPropagation={handleProgressBarClick}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                    aria-valuetext={`${Math.round(progress)}% played`}
                    aria-label="Video progress"
                  >
                    <div
                      class="progress-fill"
                      style="width: {progress}%"
                      role="presentation"
                    />
                  </div>
                </div>
              </div>
            {/if}
          </div>

          {#if content?.description && hasDescription && !isMobile}
            <div
              class="description"
              style="width: {videoDimensions.width}px"
              role="complementary"
              aria-label="Video description"
            >
              {content.description}
            </div>
          {/if}
        </main>
        <div class="close-button-container">
          <CloseButton
            close={close}
            color={textColor}
            useAbsolutePosition={false}
            marginBottom={isMobile ? "0" : "1rem"}
          />
        </div>
      </div>
    </div>
    {/if}
  </div>
{/if}

<style>
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    backdrop-filter: blur(2px);
  }

  .sizing-probe {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(255, 255, 255, 0.25);
    border-top-color: var(--spinner-color, #ffffff);
    border-radius: 50%;
    animation: video-spinner-spin 0.8s linear infinite;
  }

  @keyframes video-spinner-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .social-popup {
    background-color: transparent;
    width: auto;
    max-width: 95vw;
    max-height: 95vh;
    margin: auto;
    position: relative;
  }

  .social-content {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 95vh;
    overflow: hidden;
  }

  .social-embed-container {
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    overflow: hidden;
  }

  .video-container {
    background-color: #000;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .embed-player {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }

  .embed-player :global(iframe) {
    width: 100%;
    height: 100%;
  }

  .video-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    cursor: pointer;
  }

  .video-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .video-wrapper:hover .video-controls {
    opacity: 1;
  }

  .control-button {
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .control-button.always-visible {
    opacity: 1;
  }

  .control-button:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  .progress-bar {
    position: absolute;
    bottom: 5px;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
  }

  .progress-fill {
    height: 100%;
    background: #ffffff;
    transform: translateZ(0);
    will-change: width;
    transition: width 0.5s linear;
    pointer-events: none;
  }

  .description {
    background-color: #ffffff;
    padding: 1.5rem;
    border-radius: 8px;
    font-size: 1rem;
    line-height: 1.5;
    white-space: pre-wrap;
    color: #333;
    flex-shrink: 0;
    max-height: min(20vh, calc(95vh - 100px));
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #000000 #ffffff;
    box-sizing: border-box;
    font-family: var(--font-body), sans-serif;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .replay-button,
  .play-button {
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease;
    padding: 0;
    pointer-events: auto;
  }

  .replay-button:hover,
  .play-button:hover {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.7);
  }

  .replay-button:focus-visible,
  .play-button:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
    transform: scale(1.1);
  }

  .control-button.mute-unmute {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .video-wrapper:hover .control-button.mute-unmute {
    opacity: 1;
  }

  .control-button.mute-unmute.always-visible {
    opacity: 1;
  }

  @media (max-width: 768px) {
    .social-popup {
      width: 95vw;
      max-height: 85vh;
    }

    .social-content {
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      max-height: 85vh;
    }

    .close-button-container {
      padding-top: 0;
    }

    .description {
      display: none;
    }
  }

  .description {
    scrollbar-width: thin;
    scrollbar-color: #000000 #ffffff;
  }

  :global(::-webkit-scrollbar) {
    width: 5px;
  }
  :global(::-webkit-scrollbar-thumb) {
    background: #000000;
    border-radius: 10px;
  }

  :global(::-webkit-scrollbar-thumb:hover) {
    background: #aaaaaa;
  }

  .control-button:focus-visible,
  .progress-bar:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  .control-button:focus-visible {
    background: rgba(0, 0, 0, 0.7);
  }

  .progress-bar:focus-visible {
    height: 5px;
  }
</style>
